import React from 'react';
import { useTranslation } from 'react-i18next';
import { SiteFilter } from '../SiteFilter';
import { CurrencySelector } from '../CurrencySelector';
import type { GameFilters } from './useGameFilters';

// ---- date preset helpers (same as financial) ----
type DatePresetKey =
  | 'today'
  | 'yesterday'
  | 'last_week'
  | 'current_week'
  | 'previous_week'
  | 'current_month'
  | 'previous_month';

function toISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function startOfWeek(d: Date): Date {
  const r = new Date(d);
  const dow = r.getDay();
  const diff = dow === 0 ? -6 : 1 - dow;
  r.setDate(r.getDate() + diff);
  r.setHours(0, 0, 0, 0);
  return r;
}

function computePreset(key: DatePresetKey): { from: string; to: string } {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  switch (key) {
    case 'today':
      return { from: toISO(today), to: toISO(today) };
    case 'yesterday': {
      const y = new Date(today); y.setDate(y.getDate() - 1);
      return { from: toISO(y), to: toISO(y) };
    }
    case 'last_week': {
      const start = new Date(today); start.setDate(start.getDate() - 6);
      return { from: toISO(start), to: toISO(today) };
    }
    case 'current_week':
      return { from: toISO(startOfWeek(today)), to: toISO(today) };
    case 'previous_week': {
      const thisMon = startOfWeek(today);
      const prevMon = new Date(thisMon); prevMon.setDate(prevMon.getDate() - 7);
      const prevSun = new Date(prevMon); prevSun.setDate(prevSun.getDate() + 6);
      return { from: toISO(prevMon), to: toISO(prevSun) };
    }
    case 'current_month': {
      const first = new Date(today.getFullYear(), today.getMonth(), 1);
      return { from: toISO(first), to: toISO(today) };
    }
    case 'previous_month': {
      const firstThis = new Date(today.getFullYear(), today.getMonth(), 1);
      const lastPrev = new Date(firstThis); lastPrev.setDate(lastPrev.getDate() - 1);
      const firstPrev = new Date(lastPrev.getFullYear(), lastPrev.getMonth(), 1);
      return { from: toISO(firstPrev), to: toISO(lastPrev) };
    }
  }
}

function detectPreset(from: string, to: string): DatePresetKey | '' {
  const keys: DatePresetKey[] = [
    'today', 'yesterday', 'last_week', 'current_week', 'previous_week',
    'current_month', 'previous_month',
  ];
  for (const k of keys) {
    const p = computePreset(k);
    if (p.from === from && p.to === to) return k;
  }
  return '';
}

const GAME_TYPES = ['casino', 'live', 'crash', 'virtuals'] as const;

interface GameFilterBarProps {
  filters: GameFilters;
  showGranularity?: boolean;
  showGameType?: boolean;
}

export const GameFilterBar: React.FC<GameFilterBarProps> = ({
  filters,
  showGranularity = true,
  showGameType = true,
}) => {
  const { t } = useTranslation();
  const activePreset = detectPreset(filters.dateFrom, filters.dateTo);

  const presets: { key: DatePresetKey; label: string }[] = [
    { key: 'today',          label: t('filters.presetToday',         { defaultValue: 'Today' }) },
    { key: 'yesterday',      label: t('filters.presetYesterday',     { defaultValue: 'Yesterday' }) },
    { key: 'last_week',      label: t('filters.presetLast7Days',     { defaultValue: 'Last 7 Days' }) },
    { key: 'current_week',   label: t('filters.presetCurrentWeek',   { defaultValue: 'Current Week' }) },
    { key: 'previous_week',  label: t('filters.presetPreviousWeek',  { defaultValue: 'Previous Week' }) },
    { key: 'current_month',  label: t('filters.presetCurrentMonth',  { defaultValue: 'Current Month' }) },
    { key: 'previous_month', label: t('filters.presetPreviousMonth', { defaultValue: 'Previous Month' }) },
  ];

  const gameTypeLabels: Record<string, string> = {
    casino:   t('gamePerformance.gameTypeCasino',   { defaultValue: 'Casino' }),
    live:     t('gamePerformance.gameTypeLive',      { defaultValue: 'Live' }),
    crash:    t('gamePerformance.gameTypeCrash',     { defaultValue: 'Crash' }),
    virtuals: t('gamePerformance.gameTypeVirtuals',  { defaultValue: 'Virtuals' }),
  };

  return (
    <div className="flex flex-wrap items-end gap-4 rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
          {t('filters.preset', { defaultValue: 'Preset' })}
        </label>
        <select
          value={activePreset}
          onChange={(e) => {
            const key = e.target.value as DatePresetKey | '';
            if (!key) return;
            const { from, to } = computePreset(key);
            filters.setDateRange(from, to);
          }}
          className="mt-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-white"
        >
          <option value="">{t('filters.presetCustom', { defaultValue: 'Custom' })}</option>
          {presets.map((p) => (
            <option key={p.key} value={p.key}>{p.label}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">{t('filters.from')}</label>
        <input type="date" value={filters.dateFrom} onChange={(e) => filters.setDateFrom(e.target.value)}
          className="mt-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-white" />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">{t('filters.to')}</label>
        <input type="date" value={filters.dateTo} onChange={(e) => filters.setDateTo(e.target.value)}
          className="mt-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-white" />
      </div>
      {showGranularity && (
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
            {t('filters.granularity', { defaultValue: 'Granularity' })}
          </label>
          <select
            value={filters.granularity}
            onChange={(e) => filters.setGranularity(e.target.value as 'daily' | 'weekly' | 'monthly')}
            className="mt-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-white"
          >
            <option value="daily">{t('filters.daily', { defaultValue: 'Daily' })}</option>
            <option value="weekly">{t('filters.weekly', { defaultValue: 'Weekly' })}</option>
            <option value="monthly">{t('filters.monthly', { defaultValue: 'Monthly' })}</option>
          </select>
        </div>
      )}
      <SiteFilter selected={filters.siteIds} onChange={filters.setSiteIds} />
      <CurrencySelector selected={filters.currencyMode} onChange={filters.setCurrencyMode} />
      {showGameType && (
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
            {t('gamePerformance.gameTypeLabel', { defaultValue: 'Game Type' })}
          </label>
          <select
            value={filters.gameType ?? ''}
            onChange={(e) => filters.setGameType(e.target.value || undefined)}
            className="mt-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-white"
          >
            <option value="">{t('gamePerformance.gameTypeAll', { defaultValue: 'All Types' })}</option>
            {GAME_TYPES.map((gt) => (
              <option key={gt} value={gt}>{gameTypeLabels[gt]}</option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
};
